<script setup lang="ts">
/*
* Компонент FormAuditGroup - Форма создания и редактирования группы аудитов
*
* Основные функции:
* - Добавление участников и закрепление за ними критериев
* - Валидация полей перед сохранением
*/

// Импорт хранилища групп аудитов
// Импорт хука размеров экрана
import { useDisplay } from 'vuetify/framework'
// Импорт базовых функций Vue
import { computed, onMounted, provide, ref } from 'vue'
// Импорт хука маршрута
import { useRoute } from 'vue-router'
import { useAuditGroupStore } from '@/modules/audits/stores/useAuditGroupStore/useAuditGroupStore'
// Импорт хранилища обратной связи (загрузка, уведомления)
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
// Импорт guard'а для click:outside / keydown.esc родительского диалога
import { useDialogDismissGuard } from '@/composables/useDialogDismissGuard'

// Импорт диалога подтверждения закрытия при несохраненных изменениях
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
// Импорт компонента добавления участников в группу
import AddGroupParticipants from '@/modules/audits/stores/useAuditGroupStore/components/AddGroupParticipants.vue'
// Импорт компонента кнопки закрытия
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'

// Импорт интерфейса элемента группы аудитов
import type { IAuditGroupItem } from '@/modules/audits/stores/useAuditGroupStore/types/IAuditGroupItem'

// Определение текущего размера экрана
const { xs } = useDisplay()
// Инициализация маршрута
const route = useRoute()
// Инициализация хранилища групп аудитов
const groupStore = useAuditGroupStore()
// Инициализация хранилища обратной связи
const feedbackStore = useFeedbackStore()

// --- Закомментировано: функционал руководителя группы ---
// const apiStore = useApiStore()

// Извлечение реактивных состояний текущей и редактируемой группы
const { currentGroup, editableGroup } = storeToRefs(groupStore)

// Храним ID сотрудников, у которых не заполнен критерий
const auditorErrors = ref<Record<string, boolean>>({})

// Предоставление состояния ошибок аудиторов дочерним компонентам
provide('auditorErrors', auditorErrors)

// Флаг режима редактирования (зависит от наличия ID в маршруте)
const isEdit = computed(() => Boolean(route.params.id))

// Видимость диалога несохраненных изменений
const showUnsavedChangesDialog = ref(false)

// Видимость основной формы
const visible = ref(true)

// Вычисляемое значение заголовка карточки формы
const cardTitle = computed(() => {
  if (isEdit.value && currentGroup.value.name) return `Группа ${currentGroup.value.name}`
  if (isEdit.value) return 'Загрузка...'
  else return 'Создание новой группы'
})

// Вычисляемый текст для кнопки действия (сохранить или создать)
const actionButtonText = computed(() => {
  const id = Number(route.params.id)
  return id ? 'Сохранить' : 'Cоздать'
})

// Пустой объект группы (используется для сравнения при проверке изменений)
const emptyGroup = ref<IAuditGroupItem>({
  name: '',
  group_assignments: [],
})

/**
 * Обработчик закрытия формы
 *
 * Назначение:
 * - Проверяет наличие несохраненных изменений перед закрытием
 * - Показывает диалог подтверждения если есть изменения
 * - Закрывает форму и возвращает на родительский маршрут
 *
 * Логика:
 * - В режиме создания: проверяет есть ли введенные данные
 * - В режиме редактирования: всегда разрешает закрытие
 */
const handleClose = () => {
  if (hasFormChanges()) {
    showUnsavedChangesDialog.value = true
    return
  }

  visible.value = false
  feedbackStore.goToParentRoute()
}

const onDialogDismissAttempt = useDialogDismissGuard(handleClose)

/**
 * Проверка наличия изменений в форме
 *
 * Назначение:
 * - Определяет были ли внесены изменения в данные заявки
 * - Используется для показа диалога подтверждения при закрытии
 *
 * Возвращает:
 * - boolean: true если есть изменения, false если форма пустая
 */
const hasFormChanges = (): boolean => {
  if (!currentGroup.value) return false

  const compareWith = route?.params?.id ? editableGroup.value : emptyGroup.value

  return JSON.stringify(currentGroup.value) !== JSON.stringify(compareWith)
}

/**
 * Обработчик подтверждения закрытия формы
 *
 * Назначение:
 * - Обрабатывает результат диалога подтверждения закрытия
 * - Закрывает форму при подтверждении
 * - Оставляет форму открытой при отмене
 *
 * Входные данные:
 * - value: boolean - результат подтверждения (true - закрыть, false - отменить)
 */
const handleCloseConfirm = async (value: boolean) => {
  if (value) {
    showUnsavedChangesDialog.value = false
    visible.value = false
    await feedbackStore.goToParentRoute()
  } else {
    showUnsavedChangesDialog.value = false
  }
}

// Объект состояния ошибок валидации для каждого поля формы
const validationErrors = ref({
  name: false,
})

/**
 * Функция валидации формы перед отправкой
 *
 * Проверяет:
 * - Задано имя группы
 * - Выбраны участники группы с критериями
 *
 * Возвращает:
 * - true если валидация пройдена
 * - false если есть ошибки
 */
const validateForm = () => {
  auditorErrors.value = {}
  currentGroup.value.group_assignments.forEach(a => {
    if (!a.points || a.points.length === 0) {
      auditorErrors.value[a.employee.id] = true
    }
  })

  const hasAuditors = currentGroup.value.group_assignments.length > 0
  const allAuditorsHavePoints = currentGroup.value.group_assignments.every(a => a.points && a.points.length > 0)

  validationErrors.value = {
    name: !currentGroup.value.name.length,
  }

  const baseValid = !Object.values(validationErrors.value).some(Boolean)

  return baseValid && hasAuditors && allAuditorsHavePoints
}

// Обработчик отправки формы группы
const onGroupFormSubmit = async () => {
  const id = Number(route.params.id)

  if (!validateForm()) {
    // Вывод уведомления об ошибке валидации
    feedbackStore.showToast({
      type: 'error',
      message: 'Заполните все поля, выберите участников и их предмет проверки',
      time: 5000,
    })
    return
  }

  // Обновление существующей или создание новой группы
  if (isEdit.value && id) {
    await groupStore.updateAuditGroup(id)
  } else {
    await groupStore.createAuditGroup()
  }
}

// Хук монтирования компонента
onMounted(async () => {
  // Очищаем кэшированные деревья выборов перед загрузкой формы
  groupStore.clearTreeChoices('audit_criterion')

  // Извлекаем ID из параметров маршрута
  const id = Number(route.params.id)

  // Если нет ID, значит это режим создания
  if (!id) return

  // Если уже загружена нужная редактируемая группа, не запрашиваем повторно
  if (groupStore.currentGroup?.id === id) return

  // Асинхронный запрос данных группы по ID
  await groupStore.fetchAuditGroupById(id)
})
</script>

<template>
  <VDialog
    v-model="visible"
    max-width="1000"
    persistent
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <VCard>
      <VCardTitle class="d-flex pa-6" :class="xs ? 'px-6 py-4' : 'pa-6'">
        <div class="form_card d-flex justify-space-between w-100">
          <div class="form_card__title">
            <span class="text-h4"> {{ cardTitle }}</span>
          </div>
        </div>

        <CustomCloseButton :close-action="handleClose" />
      </VCardTitle>
      <VCardText>
        <VForm class="d-flex flex-column gap-4 px-0" @submit="onGroupFormSubmit">
          <VTextField
            v-model="currentGroup.name"
            variant="outlined"
            clearable
            placeholder="Укажите название группы"
            persistent-placeholder
            class="invisible-readonly"
            :error="validationErrors.name"
            @click:clear="currentGroup.name = ''"
          >
            <template #label>
              <span>Название<sup class="required-label">*</sup></span>
            </template>
          </VTextField>

          <div class="participant-form d-flex gap-2 align-center flex-wrap">
            <AddGroupParticipants />
          </div>

          <div class="d-flex justify-end align-center">
            <VBtn :class="{ 'flex-grow-1': xs }" @click="onGroupFormSubmit">
              {{ actionButtonText }}
            </VBtn>
          </div>
        </VForm>
      </VCardText>
    </VCard>
  </VDialog>

  <ConfirmDialog
    v-model:is-dialog-visible="showUnsavedChangesDialog"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Процесс будет прерван, данные не сохранятся"
    confirm-btn="Закрыть"
    reject-btn="Отменить"
    @confirm="handleCloseConfirm"
  />
</template>
